import React, {useEffect, useState} from "react";
import ls from "local-storage";

import {configInit, dataInit, STORE_KEY,} from "./constants";
import Loading from "../components/loading";

import {getSetting, postActivity, quickPostToApi,} from "../utils/request";
import {changeStepStringToNumber, deepAssign, detectActiveStep,} from "../utils/common";
import Close from "../components/icons/Close";

const actions = {
  setSetting: null,
  setProfile: null,
  setProfileFields: null,
};

// export const AppConfig = React.createContext(config);
export const AppStoreContext = React.createContext({
  profile: dataInit.profiles.one,
  config: configInit,
  actions: actions,
});

export const sessionShare = {
  cameraEnabled: false,
};

const postToHost = (message) => {
  const target = window.parent !== window ? window.parent : window.top;
  target.postMessage(message, "*");
};

const mergeHostProfile = (current, savedProfile) => {
  const merged = { ...current, ...savedProfile };
  if (savedProfile.fileUpload && !merged.file) {
    merged.file = savedProfile.fileUpload;
  }
  if (savedProfile.faceUpload && !merged.face) {
    merged.face = savedProfile.faceUpload;
  }
  return merged;
};

const applySavedProfile = (prevState, savedProfile) => {
  const merged = mergeHostProfile(prevState.profiles.one.data, savedProfile);
  return {
    ...prevState,
    profiles: {
      ...prevState.profiles,
      one: {
        ...prevState.profiles.one,
        data: merged,
      },
    },
  };
};

export const AppStore = ({ App }) => {
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  if (!ls.get(STORE_KEY)?.profiles) {
    ls.set(STORE_KEY, Object.assign({}, dataInit));
  }
  const [error, setError] = useState(false);
  const [data, setData] = useState(ls.get(STORE_KEY));
  const [config, setConfig] = useState(null);

  const appData = {
    data,
    config,
  };

  // get setting from host for first time
  useEffect(() => {
    postToHost({ action: "getSetting" });
    postToHost({ action: "ready" });
  }, []);

  useEffect(() => {
    setData((prevState) => {
      let newData = { ...prevState };
      newData.faceApiLoaded = false;
      return { ...newData };
    });
  }, []);

  // Handling events sent from steps

  actions.setProfile = (act, value) => {
    actions.setProfileFields({ [act]: value });
  };

  actions.setProfileFields = (updates) => {
    setData((prevState) => {
      const newData = { ...prevState };
      Object.assign(newData.profiles.one.data, updates);

      const { file, fileUpload, ...profile } = newData.profiles.one.data;
      const payload = { ...profile };
      if (fileUpload) {
        payload.fileUpload = fileUpload;
      } else if (
        Object.prototype.hasOwnProperty.call(updates, "fileUpload") &&
        !fileUpload
      ) {
        payload.fileUpload = "";
      }
      postToHost({ action: "setProfile", data: payload });
      return newData;
    });
  };

  actions.setSetting = (act, value) => {
    setData((prevState) => {
      let newData = { ...prevState };
      newData[act] = value;
      return { ...newData };
    });
  };

  actions.changeLanguage = (value) => {
    setData((prevState) => {
      let newData = { ...prevState };
      newData.lang = value;
      return { ...newData };
    });

    setTimeout(() => {
      const p = config.product.url_path;
      const d = config.product.business.domain;

      setConfig(null);
      const lang = value === -1 ? params.lang : value;
      getSetting(d, p, lang)
        .then((res) => {
          const newData = deepAssign(configInit, res.data);
          setConfig(newData);
          postToHost({ action: "setSetting", data: newData });
        })
        .catch((err) => {
          // console.log("🚀 ~ file: newIndex.js:399 ~ startInit ~ err", err);
          // setError(true);
        });
    }, 400);
  };

  actions.activity = (act, id) => {
    postActivity(act, id);
  };

  actions.setHideModal = () => {
    actions.activity("c");
    actions.setSetting("modalStatus", "hide");
    postToHost({ action: "closeModal" });
  };

  actions.updateRecommendation = (bestSize) => {
    postToHost({ action: "update_recommendation", bestSize: bestSize });
  };

  useEffect(() => {
    const toStore = {
      ...data,
      profiles: {
        ...data.profiles,
        one: {
          ...data.profiles.one,
          data: {
            ...data.profiles.one.data,
            fileUpload: "",
            file: "",
          },
        },
      },
    };
    ls.set(STORE_KEY, Object.assign(toStore));
  }, [data]);

  // Add a listener to receive events from the host
  React.useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Handling events sent from the host
  const handleMessage = (event) => {
    if (event.data.action === "config") {
      actions.setSetting("modalStatus", "hide");
      if (!event.data.data) {
        return;
      }
      if (event.data.data.clear_cache) {
        setData(dataInit);
      } else if (event.data.data.savedProfile) {
        setData((prevState) =>
          applySavedProfile(prevState, event.data.data.savedProfile)
        );
      }
      if (event.data.data.product?.business?.config?.css) {
        const styleTag = document.createElement("style");
        styleTag.innerHTML = event.data.data.product?.business?.config?.css;
        document.querySelector("head").appendChild(styleTag);
      }
      const { savedProfile: _savedProfile, clear_cache: _clearCache, ...configPayload } =
        event.data.data;
      const newData = deepAssign(configInit, configPayload);
      actions.activity("l");

      if (data.lang !== params.lang && data.lang !== -1) {
        getSetting(
          event.data.data.product.business.domain,
          event.data.data.product.url_path,
          data.lang
        ).then((res) => {
          const newData = deepAssign(configInit, res.data);
          setConfig(newData);
          postToHost({ action: "setSetting", data: newData });
        });
      } else {
        setConfig(newData);
      }

      if (!data.profiles.one.data.unit) {
        actions.setProfile(
          "unit",
          newData.product?.business?.config?.interview?.basicQuestions?.defaultUnit
        );
      }

      if (
        detectActiveStep(
          data.profiles.one.data,
          newData.product.gender,
          newData.steps,
          newData.product.business.config.interview.measure_range
        ) ===
        newData.steps.length - 1
      ) {
        if (newData.product.gender === "all" || newData.product.gender === data.profiles.one.data.gender) {
          quickPostToApi(data.profiles.one.data)
          .then((res) => {
            if (res?.recommendation?.best_size) {
              let bestSize = res?.recommendation?.best_size;
              if (res?.recommendation?.fit) {
                bestSize += ` (${res?.recommendation?.fit})`;
              }
              actions.updateRecommendation(bestSize);
              actions.activity("vr");
            }
          })
          .catch((err) => {
            // console.log("🚀 ~ file: index.js:185 ~ handleMessage ~ err:", err);
          });
        }
      }
    }

    if (event.data.action === "clearCache") {
      ls.set(STORE_KEY, Object.assign(dataInit));
    }

    if (event.data.action === "openModal") {
      const { step } = event.data.data;
      actions.setSetting("modalStatus", "show");
      actions.setSetting("activeStep", changeStepStringToNumber(step));
      actions.activity("o");
    }
  };

  if (error) {
    return <h1>we have error</h1>;
  }
  if (config === null) {
    return (
      <div className="">
        <div className={`modal `}>
          <div className="modal__overlay">&nbsp;</div>
          <div className={`modal__content `}>
            <button className="modal__close">
              <Close />
            </button>
            <Loading className="loader" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <AppStoreContext.Provider
      value={{
        data: appData.data,
        config: appData.config,
        actions,
      }}
    >
      <App />
    </AppStoreContext.Provider>
  );
};

export const withAppStore = (Comp) => (props) =>
  (
    <AppStoreContext.Consumer>
      {({ data, config, actions }) => (
        <Comp {...props} config={config} data={data} actions={actions} />
      )}
    </AppStoreContext.Consumer>
  );
